import { ecommerceApi as ecommerceClient, accountingApi } from '../config/apiClient';
import { ECOMMERCE_API_URL } from '../config/api';
import type { ShowcaseCardStyle } from '../components/e-commerce/components/showcaseCardStyle';

const API_BASE_URL = `${ECOMMERCE_API_URL}/api/ecommerce`;

// Accounting API URL (Inventory için)
const ACCOUNTING_API_URL = process.env.REACT_APP_ACCOUNTINGAPI_URL || 'http://localhost:5237';
const INVENTORY_API_BASE_URL = `${ACCOUNTING_API_URL}/api/Inventory`;

console.log('🔧 [API CONFIG] E-ticaret API URL:', API_BASE_URL);
console.log('🔧 [API CONFIG] Inventory API URL:', INVENTORY_API_BASE_URL);

type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

export interface TrustStripItem {
  value?: string;
  label?: string;
  /** Sayının yanında gösterilen emoji. İkon seçiliyse ikon önceliklidir. */
  emoji?: string;
  /** support: kulaklık, person: kişi, receipt: fiş */
  icon?: 'support' | 'person' | 'receipt' | '';
}

export type TrustStripStyle = 'inline' | 'columns' | 'editorial' | 'icons';

export interface TrustStripSettings {
  items?: TrustStripItem[];
  backgroundColor?: string;
  textColor?: string;
  mutedColor?: string;
  /** inline | columns | editorial | icons */
  style?: TrustStripStyle;
  /** Sayıları kalın yaz. Boşsa açık kabul edilir. */
  valueBold?: boolean;
  /** Şerit görünüme gelince sayıyı sıfırdan hedefe say. Boşsa açık kabul edilir. */
  animateOnScroll?: boolean;
}

export interface GeneralConfigData {
  id?: string;
  companyId?: string;
  siteName?: string;
  homePageModuleStyle?: string;
  faviconUrl?: string;
  /** Paylaşım / OG görseli (og:image). Boşsa storefront header logosuna düşer. */
  metaImage?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  layoutType?: string;
  containerMaxWidth?: number;
  homePageExcludeFromGrid?: boolean;
  whatsAppEnabled?: boolean;
  whatsAppPhone?: string;
  whatsAppMessage?: string;
  /** false: vitrinde Destek Taleplerim menüsü ve sayfası gizlenir. Varsayılan açık. */
  supportEnabled?: boolean;
  /** Site geneli marka rengi (vitrin --store-accent). */
  primaryColor?: string;
  /** Marka rengi hover tonu. */
  secondaryColor?: string;
  /** Site geneli yazı tipi (CSS font-family). */
  fontFamily?: string;
  showTrustStrip?: boolean;
  trustStripSettingsJson?: string;
}

export const ECOMMERCE_BOXED_LAYOUT_TYPE = 'boxed';
export const ECOMMERCE_MODERN_LAYOUT_TYPE = 'modern';
export const ECOMMERCE_DEFAULT_CONTAINER_MAX_WIDTH = 1440;

export const ECOMMERCE_PAGE_WIDTH_PRESETS = {
  narrow: 1200,
  medium: 1440,
  wide: 1600,
} as const;

export type EcommercePageWidthPresetId = keyof typeof ECOMMERCE_PAGE_WIDTH_PRESETS | 'full';

const runtimeCache = new Map<string, CacheEntry<any>>();

const getCached = <T>(key: string): T | null => {
  const hit = runtimeCache.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expiresAt) {
    runtimeCache.delete(key);
    return null;
  }
  return hit.value as T;
};

const setCached = <T>(key: string, value: T, ttlMs: number) => {
  runtimeCache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs,
  });
};

const invalidateByPrefix = (prefix: string) => {
  runtimeCache.forEach((_, key) => {
    if (key.startsWith(prefix)) {
      runtimeCache.delete(key);
    }
  });
};

const PRODUCTS_CACHE_TTL_MS = 45_000;
const CATEGORIES_CACHE_TTL_MS = 120_000;
const GENERAL_CONFIG_CACHE_TTL_MS = 20_000;

/**
 * FAZ 5: Artık X-Company-Id header'ı EKLEMEZ, `undefined` döner.
 *
 * Bu çağrılar e-ticaret panelinden yapılıyor ve şirket kimliği `userDetails.companyId`
 * değerinden geliyordu — yani oturumdaki kullanıcının kendi şirketi, token'daki
 * `companyId` claim'i ile aynı değer. Sunucu artık tenant'ı claim'den çözdüğü için
 * header gereksiz.
 *
 * Kaldırılmasının ikinci ve daha önemli sebebi: elle eklenen header, apiClient
 * interceptor'ının "token claim'i mutlak otoritedir, bayat localStorage'ı override eder"
 * kuralını ATLIYORDU (interceptor explicit header'a dokunmuyor). `userDetails` token'a
 * göre bayatsa header claim'den farklı çıkıp 403 COMPANY_ID_MISMATCH üretebiliyordu.
 *
 * İmza korunuyor: 22 çağrı yeri `companyId` parametresini URL/gövde için de kullanıyor,
 * dolayısıyla parametre kaldırılmadı.
 */
const withCompanyHeader = (_companyId?: string): undefined => undefined;

// Header Config API
export const getHeaderConfig = async (companyId?: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/header`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Header config getirme hatası:', error);
    throw error;
  }
};

export const saveHeaderConfig = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/header/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Header config kaydetme hatası:', error);
    throw error;
  }
};

// Menu Config API
export const getMenuConfig = async (companyId?: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/menu`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu config getirme hatası:', error);
    throw error;
  }
};

export const saveMenuConfig = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu config kaydetme hatası:', error);
    throw error;
  }
};

// E-ticaret kategorileri - hiyerarşi bilgisiyle (parentId, level) - CategoriesController
// X-Company-Id header otomatik olarak apiClient interceptor'ı tarafından eklenir
export const getPublicCategoriesWithHierarchy = async (_companyId?: string) => {
  try {
    const cacheKey = `publicCategories:${_companyId || 'default'}`;
    const cached = getCached<any[]>(cacheKey);
    if (cached) return cached;

    const response = await ecommerceClient.get(`${API_BASE_URL}/categories`);
    const data = response.data?.data || response.data;
    const normalized = Array.isArray(data) ? data : [];
    // Boş listeyi cache'leme: timeout/yanlış tenant sonrası 2 dk boş kalmasın.
    if (normalized.length > 0) {
      setCached(cacheKey, normalized, CATEGORIES_CACHE_TTL_MS);
    }
    return normalized;
  } catch (error) {
    console.error('Hiyerarşik kategoriler getirme hatası:', error);
    return [];
  }
};

// Menu Categories API
export const getMenuCategories = async (_companyId: string, configId: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/menu/categories/${configId}`,
      withCompanyHeader(_companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorileri getirme hatası:', error);
    throw error;
  }
};

export const saveMenuCategory = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/categories/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorisi kaydetme hatası:', error);
    throw error;
  }
};

export const reorderMenuCategories = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/categories/reorder`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorileri sıralama hatası:', error);
    throw error;
  }
};

// General Config API (Favicon)
export const getGeneralConfig = async (_companyId?: string): Promise<{ success?: boolean; data?: GeneralConfigData }> => {
  try {
    const cacheKey = `generalConfig:${_companyId || 'default'}`;
    const cached = getCached<any>(cacheKey);
    if (cached) return cached;

    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config`,
      withCompanyHeader(_companyId)
    );
    setCached(cacheKey, response.data, GENERAL_CONFIG_CACHE_TTL_MS);
    return response.data;
  } catch (error) {
    console.error('General config getirme hatası:', error);
    throw error;
  }
};

export const saveGeneralConfig = async (data: GeneralConfigData) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/save`,
      data,
      withCompanyHeader(data.companyId)
    );
    invalidateByPrefix('generalConfig:');
    return response.data;
  } catch (error) {
    console.error('General config kaydetme hatası:', error);
    throw error;
  }
};

// ===== B2B Ayarları (Faz 5.1) =====
export interface B2BSettingsData {
  configId?: string | null;
  storeMode?: string;
  hidePricesWhenLoggedOut?: boolean;
  /** true: fiyatları yalnızca onaylı B2B (bayi) üyeler görür */
  pricesVisibleToB2BOnly?: boolean;
  allowB2CRegistration?: boolean;
  /** Hibritte B2C kullanıcı girişi. storeMode hybrid kalır; false ise bireysel giriş/kayıt kapanır. */
  allowB2CLogin?: boolean;
  /** Vitrin bayi girişinde "Aquasoft Hesabımla" seçeneği. Varsayılan açık. */
  allowAquasoftLogin?: boolean;
  /** Aquasoft şube kullanıcılarının (BranchesId dolu) vitrine girişi. Varsayılan açık. */
  allowBranchLogin?: boolean;
  /** Şube yönetici panellerinde B2B Siparişler menüsü şirket genelinde açık mı. Varsayılan kapalı. */
  allowBranchOrderPanel?: boolean;
  b2bPricesVatExcluded?: boolean;
  storeCurrency?: string;
  exchangeRateMode?: string;
  manualExchangeRate?: number | null;
  showExchangeRateInHeader?: boolean;
  /** true + TRY dışı mağaza: bireysel (B2C) müşteri fiyatları TL görür ve TL öder */
  b2cPricesInTry?: boolean;
  onlineCollectionBankAccountId?: number | null;
}

export const getB2BSettings = async (companyId?: string): Promise<{ success?: boolean; data?: B2BSettingsData }> => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config/b2b`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('B2B ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveB2BSettings = async (data: B2BSettingsData, companyId?: string) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/b2b/save`,
      data,
      withCompanyHeader(companyId)
    );
    invalidateByPrefix('generalConfig:');
    return response.data;
  } catch (error) {
    console.error('B2B ayarları kaydetme hatası:', error);
    throw error;
  }
};


export interface StorefrontLanguageItem {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isActive: boolean;
  isDefault: boolean;
  isRtl: boolean;
  displayOrder: number;
}

export interface LanguageSettingsData {
  showSwitcher: boolean;
  defaultLanguage: string;
  languages: StorefrontLanguageItem[];
}

export const getLanguageSettings = async (
  companyId?: string
): Promise<{ success?: boolean; data?: LanguageSettingsData; catalog?: StorefrontLanguageItem[] }> => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config/languages`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Dil ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveLanguageSettings = async (
  data: LanguageSettingsData,
  companyId?: string
) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/languages/save`,
      data,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Dil ayarları kaydetme hatası:', error);
    throw error;
  }
};

export interface GoogleSearchSitelink {
  id: string;
  title: string;
  url: string;
}

export interface GoogleSearchFaq {
  id: string;
  question: string;
  answer: string;
}

export interface GoogleSearchSettingsData {
  enabled: boolean;
  title: string;
  description: string;
  websiteUrl: string;
  knowledgeName: string;
  knowledgeDescription: string;
  address: string;
  phone: string;
  email: string;
  logoUrl: string;
  sitelinks: GoogleSearchSitelink[];
  faqs: GoogleSearchFaq[];
}

export const getGoogleSearchSettings = async (
  companyId?: string
): Promise<{ success?: boolean; data?: GoogleSearchSettingsData }> => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config/google-search`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Google arama ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveGoogleSearchSettings = async (
  data: GoogleSearchSettingsData,
  companyId?: string
) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/google-search/save`,
      data,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Google arama ayarları kaydetme hatası:', error);
    throw error;
  }
};

// Manuel çeviri (override) sözlüğü: { "Kaynak metin": { "en": "...", "de": "..." } }
export type TranslationOverrides = Record<string, Record<string, string>>;

export const getTranslationOverrides = async (
  companyId?: string
): Promise<{ success?: boolean; data?: TranslationOverrides }> => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config/translations`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Manuel çeviriler getirme hatası:', error);
    throw error;
  }
};

export const saveTranslationOverrides = async (
  overrides: TranslationOverrides,
  companyId?: string
) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/translations/save`,
      { Overrides: overrides },
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Manuel çeviriler kaydetme hatası:', error);
    throw error;
  }
};

// Makineyle ön doldurma: kaynak metinleri hedef dile çevirip taslak döndürür.
export const machineTranslateTexts = async (
  target: string,
  texts: string[],
  companyId?: string,
  source: string = 'tr'
): Promise<string[]> => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/public/translate`,
      { Source: source, Target: target, Texts: texts },
      withCompanyHeader(companyId)
    );
    const translations = response?.data?.translations;
    return Array.isArray(translations) ? translations : texts;
  } catch (error) {
    console.error('Makine çevirisi hatası:', error);
    return texts;
  }
};

// Site Background API
export const getSiteBackgrounds = async (_companyId?: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/backgrounds`);
    return response.data;
  } catch (error) {
    console.error('Site backgrounds getirme hatası:', error);
    throw error;
  }
};

export const getActiveSiteBackground = async (_companyId?: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/backgrounds/active`);
    return response.data;
  } catch (error) {
    console.error('Aktif site background getirme hatası:', error);
    throw error;
  }
};

export const saveSiteBackground = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/backgrounds/save`, data);
    return response.data;
  } catch (error) {
    console.error('Site background kaydetme hatası:', error);
    throw error;
  }
};

// Product Modules API
export const getProductModules = async (_companyId: string, moduleType?: string) => {
  try {
    const url = moduleType 
      ? `${API_BASE_URL}/product-modules?moduleType=${moduleType}`
      : `${API_BASE_URL}/product-modules`;
    const response = await ecommerceClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Product modules getirme hatası:', error);
    throw error;
  }
};

export const saveProductModule = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/product-modules/save`, data);
    return response.data;
  } catch (error) {
    console.error('Product module kaydetme hatası:', error);
    throw error;
  }
};

export const deleteProductModule = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/product-modules/delete`, data);
    return response.data;
  } catch (error) {
    console.error('Product module silme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: E-ticarete açık ürünleri getir (SQL'den IsEcommerce = 1)
export const getEcommerceProducts = async (companyId: string) => {
  try {
    const cacheKey = `ecommerceProducts:${companyId || 'default'}`;
    const cached = getCached<any[]>(cacheKey);
    if (cached) return cached;

    console.log('🛒 [ECOMMERCE PRODUCTS] SQL\'den e-ticarete açık ürünler çekiliyor:', companyId);
    const response = await accountingApi.get(`${INVENTORY_API_BASE_URL}/items/ecommerce`);
    console.log('✅ [ECOMMERCE PRODUCTS] Başarılı:', response.data.length, 'ürün');
    const products = Array.isArray(response.data) ? response.data : [];
    setCached(cacheKey, products, PRODUCTS_CACHE_TTL_MS);
    return products;
  } catch (error) {
    console.error('❌ [ECOMMERCE PRODUCTS] E-ticaret ürünleri getirme hatası:', error);
    throw error;
  }
};

export const getEcommerceProductsPaged = async (options: {
  page?: number;
  pageSize?: number;
  search?: string;
}) => {
  const page = Math.max(1, options.page || 1);
  const pageSize = Math.min(50, Math.max(10, options.pageSize || 30));
  const response = await accountingApi.post(`${INVENTORY_API_BASE_URL}/items/paged`, {
    pageNumber: page,
    pageSize,
    searchTerm: options.search?.trim() || undefined,
    isActive: true,
    isEcommerce: true,
    excludeVariantChildren: true,
    sortColumn: 'name',
    sortDirection: 'asc',
  });
  const payload = response?.data || {};
  const items = payload.items || payload.Items || [];
  const totalCount = Number(payload.totalCount ?? payload.TotalCount ?? 0);
  return {
    items: Array.isArray(items) ? items : [],
    totalCount,
    page,
    pageSize,
    hasMore: page * pageSize < totalCount,
  };
};

export const getEcommerceProductsByIds = async (productIds: string[]) => {
  const ids = [...new Set((productIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  const found: any[] = [];
  for (let i = 0; i < ids.length; i += 8) {
    const chunk = ids.slice(i, i + 8);
    const rows = await Promise.all(
      chunk.map(async (id) => {
        try {
          const res = await accountingApi.get(`${INVENTORY_API_BASE_URL}/items/${id}`);
          return res?.data?.data ?? res?.data ?? { id, name: id };
        } catch {
          return { id, name: id };
        }
      })
    );
    found.push(...rows.filter(Boolean));
  }
  return found;
};

// ✅ YENİ: Belirli ID'lere sahip ürünlerin detaylarını getir
export const getProductsByIds = async (companyId: string, productIds: string[]) => {
  try {
    console.log('🔍 [PRODUCT DETAILS] Ürün detayları çekiliyor:', { companyId, count: productIds.length });
    const allProducts = await getEcommerceProducts(companyId);
    const idSet = new Set((productIds || []).map((id) => String(id)));
    
    // Sadece istenen ID'lere sahip ürünleri filtrele
    const filteredProducts = (Array.isArray(allProducts) ? allProducts : []).filter((product: any) => 
      idSet.has(String(product?.id))
    );
    
    console.log('✅ [PRODUCT DETAILS] Bulunan ürün sayısı:', filteredProducts.length);
    return filteredProducts;
  } catch (error) {
    console.error('❌ [PRODUCT DETAILS] Ürün detayları getirme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Belirli ID'lere sahip kategorilerin detaylarını getir
export const getCategoriesByIds = async (companyId: string, _configId: string, categoryIds: string[]) => {
  try {
    console.log('🔍 [CATEGORY DETAILS] Kategori detayları çekiliyor:', { companyId, count: categoryIds.length });
    const categories = await getPublicCategoriesWithHierarchy(companyId);
    const filteredCategories = (Array.isArray(categories) ? categories : [])
      .filter((category: any) => categoryIds.includes(String(category?.id || '')))
      .map((category: any) => ({
        categoryId: String(category?.id || ''),
        categoryName: String(category?.name || ''),
        categoryImageUrl: category?.imageUrl || null,
      }));

    console.log('✅ [CATEGORY DETAILS] Bulunan kategori sayısı:', filteredCategories.length);
    return filteredCategories;
  } catch (error) {
    console.error('❌ [CATEGORY DETAILS] Kategori detayları getirme hatası:', error);
    throw error;
  }
};

// ========================================
// CATEGORY SETTINGS API
// ========================================

/**
 * Kategori ayarlarını API yanıtından normalize eder
 */
export const normalizeCategorySettingsFromApi = (data: Record<string, unknown> | null | undefined) => {
  if (!data) return null;

  let titleStyles = data.titleStyles as Record<string, unknown> | undefined;
  if (!titleStyles && typeof data.titleStylesJson === 'string') {
    try {
      titleStyles = JSON.parse(data.titleStylesJson);
    } catch {
      titleStyles = undefined;
    }
  }

  const rawShowOnHomepage =
    data.showOnHomepage ??
    data.showCategoriesOnHomepage ??
    data.ShowCategoriesOnHomepage;

  const displayStyleRaw = data.displayStyle as Record<string, unknown> | string | undefined;
  const webLayout =
    (typeof displayStyleRaw === 'object' && displayStyleRaw
      ? displayStyleRaw.web ?? displayStyleRaw.Web
      : displayStyleRaw) ??
    data.homepageCategoryLayout ??
    data.HomepageCategoryLayout ??
    'grid';
  const mobileLayout =
    (typeof displayStyleRaw === 'object' && displayStyleRaw
      ? displayStyleRaw.mobile ?? displayStyleRaw.Mobile
      : undefined) ??
    data.mobileCategoryLayout ??
    data.MobileCategoryLayout ??
    'list';

  const menuDropdownStyleRaw = String(
    data.menuDropdownStyle ?? data.MenuDropdownStyle ?? 'minimal'
  ).toLowerCase();
  const menuDropdownStyle =
    menuDropdownStyleRaw === 'list'
      ? 'list' as const
      : menuDropdownStyleRaw === 'classic'
        ? 'classic' as const
        : 'minimal' as const;

  const extraFromStyles = titleStyles as {
    showOnProductsPage?: boolean;
    menuDropdownEnabled?: boolean;
    enableProductPicker?: boolean;
  } | undefined;
  const showOnProductsPage = Boolean(
    extraFromStyles?.showOnProductsPage
    ?? data.showOnProductsPage
    ?? data.ShowOnProductsPage
  );
  const menuDropdownEnabled = extraFromStyles?.menuDropdownEnabled !== false
    && data.menuDropdownEnabled !== false
    && data.MenuDropdownEnabled !== false;
  const enableProductPicker = extraFromStyles && 'enableProductPicker' in extraFromStyles
    ? extraFromStyles.enableProductPicker === true
    : data.enableProductPicker === true || data.EnableProductPicker === true
      ? true
      : undefined;
  if (titleStyles && ('showOnProductsPage' in titleStyles || 'menuDropdownEnabled' in titleStyles || 'enableProductPicker' in titleStyles)) {
    const {
      showOnProductsPage: _showOnProductsPage,
      menuDropdownEnabled: _menuDropdownEnabled,
      enableProductPicker: _enableProductPicker,
      ...cleanTitleStyles
    } = titleStyles as Record<string, unknown>;
    titleStyles = cleanTitleStyles;
  }

  return {
    showOnHomepage: rawShowOnHomepage === undefined ? true : Boolean(rawShowOnHomepage),
    showOnProductsPage,
    enableProductPicker,
    menuDropdownEnabled,
    targetPage: String(data.targetPage ?? data.TargetPage ?? 'homepage') || 'homepage',
    insertAfter: String(data.insertAfter ?? data.InsertAfter ?? 'after-trust') || 'after-trust',
    displayStyle: {
      web: String(webLayout),
      mobile: String(mobileLayout),
    },
    itemsPerRow: Number(data.itemsPerRow ?? data.categoriesPerRow ?? data.CategoriesPerRow ?? 4),
    titleStyles: titleStyles || {
      fontSize: 16,
      color: '#ffffff',
      bold: true,
      italic: false,
      fontFamily: 'Inter',
      strokeWidth: 0,
      strokeColor: '#000000',
    },
    title: String(data.title ?? data.homepageCategoryTitle ?? data.HomepageCategoryTitle ?? 'Kategoriler'),
    menuDropdownStyle,
    menuDropdownColumns: Number(data.menuDropdownColumns ?? data.MenuDropdownColumns ?? 4),
    menuDropdownMaxHeight: Number(data.menuDropdownMaxHeight ?? data.MenuDropdownMaxHeight ?? 400),
  };
};

export const getCategorySettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📂 [CATEGORY] SQL\'den kategori ayarları çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${ECOMMERCE_API_URL}/api/ecommerce/admin/category-settings/${configId}`);
    console.log('✅ [CATEGORY] Kategori ayarları başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CATEGORY] Kategori ayarları getirme hatası:', error);
    throw error;
  }
};

/**
 * Kategori ayarlarını kaydeder
 */
export const saveCategorySettings = async (data: any) => {
  try {
    const url = `${ECOMMERCE_API_URL}/api/ecommerce/admin/category-settings/save`;
    console.log('💾 [CATEGORY] SQL\'e kategori ayarları kaydediliyor:', data);
    console.log('🌐 [CATEGORY] API URL:', url);
    const response = await ecommerceClient.post(url, data);
    console.log('✅ [CATEGORY] Kategori ayarları başarıyla kaydedildi');
    console.log('📥 [CATEGORY] Backend yanıtı:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ [CATEGORY] Kategori ayarları kaydetme hatası:', error);
    console.error('❌ [CATEGORY] Hata detayı:', {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data
    });
    throw error;
  }
};

// ========================================
// SLIDER API
// ========================================

export const getSliderSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🎠 [SLIDER] SQL\'den slider ayarları çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/slider/${configId}`);
    console.log('✅ [SLIDER] Slider ayarları başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slider ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveSliderSettings = async (data: any) => {
  try {
    console.log('💾 [SLIDER] SQL\'e slider ayarları kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/save-settings`, data);
    console.log('✅ [SLIDER] Slider ayarları başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slider ayarları kaydetme hatası:', error);
    throw error;
  }
};

export const addSlide = async (data: any) => {
  try {
    console.log('➕ [SLIDER] Yeni slide ekleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/add-slide`, data);
    console.log('✅ [SLIDER] Slide başarıyla eklendi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide ekleme hatası:', error);
    throw error;
  }
};

export const updateSlide = async (data: any) => {
  try {
    console.log('✏️ [SLIDER] Slide güncelleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/update-slide`, data);
    console.log('✅ [SLIDER] Slide başarıyla güncellendi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide güncelleme hatası:', error);
    throw error;
  }
};

export const toggleSlide = async (data: any) => {
  try {
    console.log('🔄 [SLIDER] Slide durumu değiştiriliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/toggle-slide`, data);
    console.log('✅ [SLIDER] Slide durumu başarıyla değiştirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide durumu değiştirme hatası:', error);
    throw error;
  }
};

export const deleteSlide = async (data: any) => {
  try {
    console.log('🗑️ [SLIDER] Slide siliniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/delete-slide`, data);
    console.log('✅ [SLIDER] Slide başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide silme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Product Settings API
export const getProductSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🛍️ [PRODUCT SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-settings/${configId}`);
    console.log('✅ [PRODUCT SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveProductSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-settings/save`, data);
    console.log('✅ [PRODUCT SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Product Detail Settings API
export const getProductDetailSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📄 [PRODUCT DETAIL SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-detail-settings/${configId}`);
    console.log('✅ [PRODUCT DETAIL SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT DETAIL SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveProductDetailSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT DETAIL SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-detail-settings/save`, data);
    console.log('✅ [PRODUCT DETAIL SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT DETAIL SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Footer Settings API
export const getFooterSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🦶 [FOOTER SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/footer/${configId}`);
    console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FOOTER SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveFooterSettings = async (data: any) => {
  try {
    console.log('💾 [FOOTER SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/footer/save`, data);
    console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FOOTER SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Cart Settings API
export const getCartSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🛒 [CART SETTINGS] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/cart-settings/${configId}`);
    console.log('✅ [CART SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CART SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveCartSettings = async (data: any) => {
  try {
    console.log('💾 [CART SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/cart-settings/save`, data);
    console.log('✅ [CART SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CART SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

export const getLoginPageSettings = async (companyId: string, configId: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/login-page-settings/${configId}`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('❌ [LOGIN PAGE SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveLoginPageSettings = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/login-page-settings/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('❌ [LOGIN PAGE SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: About Settings API
export const getAboutSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [ABOUT SETTINGS] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/about-settings/${configId}`);
    console.log('✅ [ABOUT SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveAboutSettings = async (data: any) => {
  try {
    console.log('💾 [ABOUT SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-settings/save`, data);
    console.log('✅ [ABOUT SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: About Tabs (Dynamic Pages) API
export const getAboutTabs = async (configId: string, pageType: string = 'AboutTab') => {
  try {
    console.log(`📖 [${pageType.toUpperCase()}] SQL den dinamik sekmeler çekiliyor:`, configId);
    const url = `${API_BASE_URL}/admin/about-tabs/${configId}?pageType=${pageType}`;
    const response = await ecommerceClient.get(url);
    console.log(`✅ [${pageType.toUpperCase()}] Sekmeler başarıyla getirildi:`, response.data.data?.length || 0, 'sekme');
    return response.data;
  } catch (error) {
    console.error(`❌ [${pageType.toUpperCase()}] Sekmeler getirme hatası:`, error);
    throw error;
  }
};

export const saveAboutTab = async (data: any) => {
  try {
    console.log('💾 [ABOUT TABS] SQL e sekme kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-tabs/save`, data);
    console.log('✅ [ABOUT TABS] Sekme başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT TABS] Sekme kaydetme hatası:', error);
    throw error;
  }
};

export const deleteAboutTab = async (tabId: string, companyId: string) => {
  try {
    console.log('🗑️ [ABOUT TABS] SQL den sekme siliniyor:', tabId, companyId);
    // 🔒 MULTI-TENANT: companyId eklendi
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-tabs/delete`, { 
      id: tabId,
      companyId: companyId
    });
    console.log('✅ [ABOUT TABS] Sekme başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT TABS] Sekme silme hatası:', error);
    throw error;
  }
};

// ========================================
// CUSTOM PAGES API (Kullanıcı Tanımlı Sayfalar)
// ========================================

export interface CustomPageData {
  id?: string;
  configId: string;
  title: string;
  subtitle?: string;
  content?: string;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  name?: string;
  isActive?: boolean;
  showInMenu?: boolean;
  showInFooter?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getCustomPages = async (configId: string) => {
  try {
    console.log('📖 [CUSTOM PAGES] Sayfalar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/custom-pages/${configId}`);
    console.log('✅ [CUSTOM PAGES] Sayfalar getirildi:', response.data.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa listesi hatası:', error);
    throw error;
  }
};

export const saveCustomPage = async (data: CustomPageData) => {
  try {
    console.log('💾 [CUSTOM PAGES] Sayfa kaydediliyor:', data?.title);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/custom-pages/save`, data);
    console.log('✅ [CUSTOM PAGES] Sayfa kaydedildi:', response.data?.data?.id);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa kaydetme hatası:', error);
    throw error;
  }
};

export const deleteCustomPage = async (pageId: string) => {
  try {
    console.log('🗑️ [CUSTOM PAGES] Sayfa siliniyor:', pageId);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/custom-pages/delete`, { id: pageId });
    console.log('✅ [CUSTOM PAGES] Sayfa silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa silme hatası:', error);
    throw error;
  }
};

// ========================================
// LEGAL DOCUMENTS API (Yasal Sözleşmeler)
// ========================================

export interface LegalDocumentData {
  id?: string;
  configId: string;
  companyId?: string;
  documentType?: string;
  title: string;
  slug?: string;
  content?: string;
  isActive?: boolean;
  showInFooter?: boolean;
  showInSwitcher?: boolean;
  switcherGroup?: string;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getLegalDocuments = async (configId: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/legal-documents/${configId}`);
    return response.data;
  } catch (error) {
    console.error('❌ [LEGAL DOCS] Belge listesi hatası:', error);
    throw error;
  }
};

export const saveLegalDocument = async (data: LegalDocumentData) => {
  try {
    // CompanyId JWT claim'den alınır — body'de gönderilmez (Yöntem 1)
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/legal-documents/save`, {
      Id: data.id,
      ConfigId: data.configId,
      DocumentType: data.documentType,
      Title: data.title,
      Slug: data.slug,
      Content: data.content,
      IsActive: data.isActive,
      ShowInFooter: data.showInFooter,
      ShowInSwitcher: data.showInSwitcher,
      SwitcherGroup: data.switcherGroup,
      DisplayOrder: data.displayOrder,
    });
    return response.data;
  } catch (error) {
    console.error('❌ [LEGAL DOCS] Belge kaydetme hatası:', error);
    throw error;
  }
};

export const deleteLegalDocument = async (documentId: string) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/legal-documents/delete`, { Id: documentId });
    return response.data;
  } catch (error) {
    console.error('❌ [LEGAL DOCS] Belge silme hatası:', error);
    throw error;
  }
};

export const seedLegalDefaults = async (configId: string) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/legal-documents/seed-defaults/${configId}`, {});
    return response.data;
  } catch (error) {
    console.error('❌ [LEGAL DOCS] Taslak oluşturma hatası:', error);
    throw error;
  }
};

// ========================================
// CUSTOM FORMS API (Kullanıcı Tanımlı Formlar)
// ========================================

export type CustomFormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'city_district'
  | 'city_branch'
  | 'image'
  | 'section'
  | 'consent';

export interface CustomFormField {
  id: string;
  type: CustomFormFieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: string[];
  /** Onay alanı: etikette tıklanabilir metin (örn. KVKK Aydınlatma Metni) */
  linkText?: string;
  /** Onay alanı: modal başlığı */
  legalTitle?: string;
  /** Onay alanı: modalda gösterilecek metin */
  legalText?: string;
  /** Onay alanı: Yasal Sözleşmeler belgesi slug'ı — tıklanınca içeriği çekilir */
  legalDocumentSlug?: string;
  legalDocumentTitle?: string;
  /** Onay alanı: sitedeki özel sayfa slug'ı — tıklanınca içeriği çekilir */
  legalPageSlug?: string;
  legalPageTitle?: string;
}

export interface CustomFormData {
  id?: string;
  configId: string;
  title: string;
  slug?: string;
  description?: string;
  submitButtonText?: string;
  successMessage?: string;
  notifyEmail?: string;
  fieldsJson?: string;
  isActive?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomFormSubmission {
  id: string;
  formId: string;
  companyId: string;
  dataJson?: string | null;
  submitterIp?: string | null;
  userAgent?: string | null;
  pageSlug?: string | null;
  isRead: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export const getCustomForms = async (configId: string) => {
  try {
    console.log('📖 [CUSTOM FORMS] Formlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/forms/${configId}`);
    console.log('✅ [CUSTOM FORMS] Formlar getirildi:', response.data.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form listesi hatası:', error);
    throw error;
  }
};

export const saveCustomForm = async (data: CustomFormData) => {
  try {
    console.log('💾 [CUSTOM FORMS] Form kaydediliyor:', data?.title);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/forms/save`, data);
    console.log('✅ [CUSTOM FORMS] Form kaydedildi:', response.data?.data?.id);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form kaydetme hatası:', error);
    throw error;
  }
};

export const deleteCustomForm = async (formId: string) => {
  try {
    console.log('🗑️ [CUSTOM FORMS] Form siliniyor:', formId);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/forms/delete`, { id: formId });
    console.log('✅ [CUSTOM FORMS] Form silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form silme hatası:', error);
    throw error;
  }
};

export const getCustomFormSubmissions = async (
  formId: string,
  page: number = 1,
  pageSize: number = 25,
  isRead?: boolean
) => {
  try {
    const params: Record<string, string | number> = { page, pageSize };
    if (typeof isRead === 'boolean') params.isRead = String(isRead);
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/forms/${formId}/submissions`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Gönderim listesi hatası:', error);
    throw error;
  }
};

export const markCustomFormSubmissionRead = async (id: string, isRead: boolean = true) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/forms/submissions/mark-read`,
      { id, isRead }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] İşaretleme hatası:', error);
    throw error;
  }
};

export const deleteCustomFormSubmission = async (id: string) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/forms/submissions/delete`,
      { id }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Gönderim silme hatası:', error);
    throw error;
  }
};

// ========================================
// SERVICE SETTINGS API (Hizmetler Sabit Tab)
// ========================================

export const getServiceSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [SERVICE SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/service-settings/${configId}`);
    console.log('✅ [SERVICE SETTINGS] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SERVICE SETTINGS] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveServiceSettings = async (data: any) => {
  try {
    console.log('💾 [SERVICE SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/service-settings/save`, data);
    console.log('✅ [SERVICE SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SERVICE SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ========================================
// PRODUCT SOLUTION SETTINGS API
// ========================================

/**
 * CompanyId'ye göre Product Solution ayarlarını getirir
 */
/**
 * ConfigId'ye göre Product Solution ayarlarını getirir
 */
export const getProductSolutionSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [PRODUCT SOLUTION] SQL den ayarlar çekiliyor (ConfigId):', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-solution-settings/${configId}`);
    console.log('✅ [PRODUCT SOLUTION] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SOLUTION] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * Product Solution ayarlarını kaydeder (Create veya Update)
 */
export const saveProductSolutionSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT SOLUTION] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-solution-settings/save`, data);
    console.log('✅ [PRODUCT SOLUTION] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SOLUTION] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ============================================
// 📞 CONTACT SETTINGS API
// ============================================

/**
 * ConfigId ile İletişim Ayarlarını Getir
 */
export const getContactSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [CONTACT] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/contact-settings/${configId}`);
    console.log('✅ [CONTACT] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CONTACT] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * İletişim Ayarlarını Kaydet
 */
export const saveContactSettings = async (data: any) => {
  try {
    console.log('💾 [CONTACT] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/contact-settings/save`, data);
    console.log('✅ [CONTACT] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CONTACT] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ============================================
// 🍽️ RESTAURANT SETTINGS API
// ============================================

export const getRestaurantSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [RESTAURANT] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/restaurants-settings/${configId}`);
    console.log('✅ [RESTAURANT] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveRestaurantSettings = async (data: {
  configId: string;
  heroEnabled: boolean;
  heroTitle: string;
  heroDescription: string;
  heroBackgroundColor: string;
  mapAccentColor?: string;
  pageTemplate?: 'classic' | 'province-map';
  brandLogos?: Array<{ url: string; alt?: string }>;
  paymentIcons?: Array<{ url: string; alt?: string }>;
}) => {
  try {
    console.log('💾 [RESTAURANT] SQL e branch page ayarları kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/restaurants-settings/save`, data);
    console.log('✅ [RESTAURANT] Branch page ayarları kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT] Branch page ayarları kaydetme hatası:', error);
    throw error;
  }
};

/**
 * Restoran sayfasındaki şubelerin e-ticaret görünüm alanlarını (ImageUrl, MapUrl,
 * DisplayOrder, ShowOnEcommerce) günceller. Şube listesi mevcut Branches tablosundan
 * gelir; bu endpoint yalnızca o şubedeki ek alanları günceller.
 *
 * @param branchId Branch.FirebaseId veya numeric Branch.Id
 */
export const updateRestaurantBranch = async (
  branchId: string,
  data: {
    imageUrl?: string | null;
    mapUrl?: string | null;
    displayOrder?: number;
    showOnEcommerce?: boolean;
    workingHours?: string | null;
    isOpen24Hours?: boolean;
    onlyFillEmptyMap?: boolean;
    mapOnly?: boolean;
  },
) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/restaurants-settings/branch/${encodeURIComponent(branchId)}/update`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT BRANCH] Güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Ayarlarını Getir (ConfigId ile)
 */
export const getReferenceSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [REFERENCE] SQL den ayarlar çekiliyor (ConfigId):', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/reference-settings/${configId}`);
    console.log('✅ [REFERENCE] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * Referans Ayarlarını Kaydet
 */
export const saveReferenceSettings = async (data: any) => {
  try {
    console.log('💾 [REFERENCE] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/save`, data);
    console.log('✅ [REFERENCE] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

/**
 * Yeni Referans Firması Ekle
 */
export const addReferenceItem = async (data: any) => {
  try {
    console.log('➕ [REFERENCE ITEM] Yeni referans ekleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item`, data);
    console.log('✅ [REFERENCE ITEM] Referans başarıyla eklendi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans ekleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Firmasını Güncelle
 */
export const updateReferenceItem = async (id: string, data: any) => {
  try {
    console.log('🔄 [REFERENCE ITEM] Referans güncelleniyor:', id, data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item/${id}/update`, data); // ✅ POST ve /update eklendi
    console.log('✅ [REFERENCE ITEM] Referans başarıyla güncellendi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Firmasını Sil
 */
export const deleteReferenceItem = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [REFERENCE ITEM] Referans siliniyor:', id);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item/${id}/delete`, { companyId }); // ✅ POST ve companyId eklendi
    console.log('✅ [REFERENCE ITEM] Referans başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans silme hatası:', error);
    throw error;
  }
};

// ========================================
// BLOG SETTINGS API
// ========================================

/**
 * Blog Ayarlarını Getir (ConfigId ile) - sayfa ayarları + anasayfa kartı + yazılar
 */
export const getBlogSettings = async (_companyId: string, configId: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/blog-settings/${configId}`);
    return response.data;
  } catch (error) {
    console.error('❌ [BLOG] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * Blog Ayarlarını Kaydet (sayfa hero + anasayfa kartı tasarımı)
 */
export const saveBlogSettings = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/blog-settings/save`, data);
    return response.data;
  } catch (error) {
    console.error('❌ [BLOG] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

/**
 * Yeni Blog Yazısı Ekle
 */
export const addBlogPost = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/blog-settings/post`, data);
    return response.data;
  } catch (error) {
    console.error('❌ [BLOG POST] Yazı ekleme hatası:', error);
    throw error;
  }
};

/**
 * Blog Yazısını Güncelle
 */
export const updateBlogPost = async (id: string, data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/blog-settings/post/${id}/update`, data);
    return response.data;
  } catch (error) {
    console.error('❌ [BLOG POST] Yazı güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Blog Yazısını Sil
 */
export const deleteBlogPost = async (id: string, companyId: string) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/blog-settings/post/${id}/delete`, { companyId });
    return response.data;
  } catch (error) {
    console.error('❌ [BLOG POST] Yazı silme hatası:', error);
    throw error;
  }
};

// ========================================
// FAQ SETTINGS API
// ========================================

export const getFAQSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [FAQ] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/faq-settings/${configId}`);
    console.log('✅ [FAQ] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveFAQSettings = async (data: any) => {
  try {
    console.log('💾 [FAQ] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/save`, data);
    console.log('✅ [FAQ] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

export const saveFAQCategory = async (data: any) => {
  try {
    console.log('💾 [FAQ CATEGORY] SQL e kategori kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/category`, data);
    console.log('✅ [FAQ CATEGORY] Kategori başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ CATEGORY] Kategori kaydetme hatası:', error);
    throw error;
  }
};

export const deleteFAQCategory = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [FAQ CATEGORY] SQL den kategori siliniyor:', { id, companyId });
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/category/delete`, { id, companyId }); // ✅ POST ve body kullanımı
    console.log('✅ [FAQ CATEGORY] Kategori başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ CATEGORY] Kategori silme hatası:', error);
    throw error;
  }
};

export const saveFAQItem = async (data: any) => {
  try {
    console.log('💾 [FAQ ITEM] SQL e soru kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/item`, data);
    console.log('✅ [FAQ ITEM] Soru başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ ITEM] Soru kaydetme hatası:', error);
    throw error;
  }
};

export const deleteFAQItem = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [FAQ ITEM] SQL den soru siliniyor:', { id, companyId });
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/item/delete`, { id, companyId }); // ✅ POST ve body kullanımı
    console.log('✅ [FAQ ITEM] Soru başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ ITEM] Soru silme hatası:', error);
    throw error;
  }
};

// ==================== KAMPANYA (E-Ticaret Pazarlama) ====================

export interface CampaignData {
  id?: string;
  companyId?: string;
  name: string;
  description?: string;
  type: string; // birincil tür (ProductDiscount | Coupon | ...)
  /** Çoklu kampanya türleri (Trendyol tarzı birden fazla mekanik) */
  types?: string[];
  startDate: string;
  endDate: string;
  discountType: string; // Percentage | Amount
  discountValue: number;
  maxDiscountAmount?: number | null;
  minCartAmount?: number | null;
  buyQuantity?: number | null;
  payQuantity?: number | null;
  /** X al Y öde: bedava adet yalnız aynı üründen sayılsın mı. */
  buyXPayYSameProductOnly?: boolean;
  /** X al Y öde: sepette toplam en fazla kaç bedava birim (null = sınırsız). */
  maxFreeUnits?: number | null;
  /** X al Y öde: bedava seçilecek birim — Cheapest (varsayılan) | Expensive. */
  freeUnitSelection?: string | null;
  couponCode?: string | null;
  usageLimit?: number | null;
  usageLimitPerCustomer?: number | null;
  /** Kupon, kazanan otomatik indirimin üzerine binsin mi. Kapalıysa yalnız kupon. */
  allowStackWithOthers?: boolean;
  usedCount?: number;
  scopeType: string; // All | Products | Categories | Brands
  priority?: number;
  /** Özel gün / sezon anahtarı (mothers-day, black-friday, ...) */
  specialDay?: string | null;
  isActive: boolean;
  status?: string; // passive | upcoming | active | expired (backend türetir)
  productIds?: string[];
  categoryIds?: string[];
  brandIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export const getEcommerceBrands = async (companyId?: string) => {
  const response = await ecommerceClient.get(
    `${API_BASE_URL}/brands`,
    withCompanyHeader(companyId)
  );
  const payload = response?.data?.data ?? response?.data;
  return Array.isArray(payload) ? payload : [];
};

const CAMPAIGNS_BASE_URL = `${API_BASE_URL}/admin/campaigns`;

const SHOWCASES_BASE_URL = `${API_BASE_URL}/admin/showcases`;

export type ShowcaseAreaKind = 'Bestseller' | 'Trending' | 'NewArrival' | 'Outlet' | 'Custom';

export type ShowcaseAreaData = {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  kind: ShowcaseAreaKind;
  source?: string;
  badgeText?: string | null;
  badgeColor?: string | null;
  showOnHomepage: boolean;
  homepageLimit: number;
  homepageLayout: string;
  insertAfter: string;
  displayOrder: number;
  showViewAll: boolean;
  viewAllText: string;
  titleAlign?: 'left' | 'center' | 'right';
  titleFontSize?: number;
  animation?: 'FadeUp' | 'SlideLeft';
  cardStyle?: ShowcaseCardStyle | null;
  isActive: boolean;
  isPreset?: boolean;
  productCount: number;
  productIds: string[];
  previewProducts?: Array<{ id: string; name: string; imageUrl?: string | null }>;
};

export const getShowcaseAreas = async (companyId?: string, configId?: string) => {
  const params = configId ? { configId } : undefined;
  const response = await ecommerceClient.get(SHOWCASES_BASE_URL, {
    ...withCompanyHeader(companyId),
    params,
  });
  return response.data as { success: boolean; data: ShowcaseAreaData[]; count: number };
};

export const getShowcaseArea = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.get(`${SHOWCASES_BASE_URL}/${id}`, withCompanyHeader(companyId));
  return response.data as { success: boolean; data: ShowcaseAreaData };
};

export const createShowcaseArea = async (data: Partial<ShowcaseAreaData> & { configId?: string; companyId?: string }) => {
  const response = await ecommerceClient.post(SHOWCASES_BASE_URL, data, withCompanyHeader(data.companyId));
  return response.data as { success: boolean; data: ShowcaseAreaData; message?: string };
};

export const updateShowcaseArea = async (
  id: string,
  data: Partial<ShowcaseAreaData> & { configId?: string; companyId?: string }
) => {
  const response = await ecommerceClient.post(`${SHOWCASES_BASE_URL}/update/${id}`, data, withCompanyHeader(data.companyId));
  return response.data as { success: boolean; data: ShowcaseAreaData; message?: string };
};

export const toggleShowcaseArea = async (id: string, showOnHomepage: boolean, companyId?: string) => {
  const response = await ecommerceClient.post(
    `${SHOWCASES_BASE_URL}/${id}/toggle`,
    { showOnHomepage },
    withCompanyHeader(companyId)
  );
  return response.data;
};

export const deleteShowcaseArea = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.post(`${SHOWCASES_BASE_URL}/delete/${id}`, {}, withCompanyHeader(companyId));
  return response.data as { success: boolean; message?: string };
};

export const getCampaigns = async (companyId?: string, status?: string, type?: string) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (type) params.append('type', type);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}${query}`, withCompanyHeader(companyId));
  return response.data;
};

export const getCampaign = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}/${id}`, withCompanyHeader(companyId));
  return response.data;
};

export const createCampaign = async (data: CampaignData) => {
  const response = await ecommerceClient.post(CAMPAIGNS_BASE_URL, data, withCompanyHeader(data.companyId));
  return response.data;
};

export const updateCampaign = async (id: string, data: CampaignData) => {
  // Production: PUT yasak — POST /update/{id}
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/update/${id}`, data, withCompanyHeader(data.companyId));
  return response.data;
};

export const toggleCampaign = async (id: string, isActive: boolean, companyId?: string) => {
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/${id}/toggle`, { isActive }, withCompanyHeader(companyId));
  return response.data;
};

export const deleteCampaign = async (id: string, companyId?: string) => {
  // Production: DELETE yasak — POST /delete/{id}
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/delete/${id}`, {}, withCompanyHeader(companyId));
  return response.data;
};

export const getCampaignStats = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}/${id}/stats`, withCompanyHeader(companyId));
  return response.data;
};

export type CampaignUsageRow = {
  id: string;
  orderId?: string | null;
  orderNumber?: string | null;
  customer: string;
  isGuest: boolean;
  couponCode?: string | null;
  discountAmount: number;
  usedAt: string;
  reversed: boolean;
};

export type CampaignSimulateResult = {
  merchandiseSubtotal: number;
  useListPrices: boolean;
  cartDiscount: number;
  couponDiscount: number;
  totalDiscount: number;
  shippingAmount: number;
  payable: number;
  freeShipping: boolean;
  exclusiveHint?: string | null;
  applied: Array<{
    campaignId: string;
    campaignName: string;
    type: string;
    discountAmount: number;
    priority: number;
  }>;
  coupon?: {
    valid: boolean;
    applied: boolean;
    couponCode?: string | null;
    campaignName?: string | null;
    discountAmount: number;
    message?: string | null;
  } | null;
};

export const simulateCampaignCart = async (
  items: Array<{ productId: string; quantity: number }>,
  options?: { couponCode?: string; shippingAmount?: number; companyId?: string }
) => {
  const response = await ecommerceClient.post(
    `${CAMPAIGNS_BASE_URL}/simulate`,
    {
      items,
      couponCode: options?.couponCode || null,
      shippingAmount: options?.shippingAmount || 0,
    },
    withCompanyHeader(options?.companyId)
  );
  return response.data as { success: boolean; message?: string; data: CampaignSimulateResult };
};

export const getCampaignUsages = async (
  id: string,
  companyId?: string,
  page = 1,
  pageSize = 25
) => {
  const response = await ecommerceClient.get(
    `${CAMPAIGNS_BASE_URL}/${id}/usages`,
    { ...withCompanyHeader(companyId), params: { page, pageSize } }
  );
  return response.data as {
    success: boolean;
    data: CampaignUsageRow[];
    page: number;
    pageSize: number;
    total: number;
    usedCount?: number;
  };
};

// ============================================================
// B2B siparişi (tenant ↔ tenant bağlantı + gelen alış siparişleri)
// ============================================================

export interface CompanyLinkRow {
  id: number;
  role: 'buyer' | 'seller';
  /** Alıcı firmanın şubesi (null = merkez). Satıcı tarafında da dolu gelir. */
  buyerBranchId?: string | null;
  buyerBranchName?: string | null;
  sellerAccountId?: number | null;
  sellerAccountName?: string | null;
  counterpartCompanyId: string;
  counterpartCompanyName: string;
  status: 'Active' | 'Revoked';
  createdAt: string;
  revokedAt?: string | null;
}

export interface IncomingPurchaseOrderRow {
  id: string;
  /** Siparişi veren şube (null = merkez). */
  branchId?: string | null;
  sellerCompanyId: string;
  sellerCompanyName: string;
  sellerOrderNumber: string;
  status: string;
  isInvoiced: boolean;
  invoiceId?: string | null;
  invoicedAt?: string | null;
  totalAmount: number;
  currency: string;
  trackingNumber?: string | null;
  shippingCompany?: string | null;
  /** Satıcının kestiği sevk irsaliyesinin numarası (sevkiyatta otomatik oluşur). */
  sellerWaybillNumber?: string | null;
  /** Satıcının gönderdiği e-fatura ETTN (yoksa henüz gönderilmemiş). */
  sellerEInvoiceETTN?: string | null;
  sellerEInvoiceStatus?: string | null;
  /** B2B mal kabul (irsaliye onaylama): null | Approved | ApprovedWithDiscrepancy */
  receiptStatus?: string | null;
  hasReceiptDiscrepancy?: boolean;
  receiptNote?: string | null;
  receiptConfirmedAt?: string | null;
  orderedAt: string;
  itemCount: number;
  unmatchedCount: number;
}

export interface IncomingPurchaseOrderItemRow {
  id: string;
  productName: string;
  barcode?: string | null;
  stockCode?: string | null;
  unit: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  taxAmount: number;
  totalPrice: number;
  matchedInventoryItemId?: string | null;
  matchSource?: string | null;
  matchedProductName?: string | null;
  // B2B mal kabul (irsaliye onaylama): teslim alınan/hasarlı miktar + sebep
  receivedQuantity?: number | null;
  damagedQuantity?: number | null;
  receiptReason?: string | null;
}

export interface IncomingPurchaseOrderDetail extends Omit<IncomingPurchaseOrderRow, 'itemCount' | 'unmatchedCount'> {
  sellerOrderId: string;
  supplierAccountId?: number | null;
  subTotal: number;
  taxAmount: number;
  shippingAmount: number;
  discountAmount: number;
  notes?: string | null;
  items: IncomingPurchaseOrderItemRow[];
}

/**
 * Firmalar arası B2B bağlantılarını otomatik kurar: şirketin carilerini tarar,
 * vergi no / e-posta ile eşleşen Aquasoft firmalarını bağlar (eşleştirme kodu yerine geçer).
 * Kurulan yeni bağlantı sayısını döner (idempotent).
 */
export const scanCompanyLinks = async () => {
  const response = await ecommerceClient.post(`${API_BASE_URL}/company-links/scan`, {});
  return response.data as { success: boolean; created: number };
};

/** Şirketin tüm ticaret bağlantıları (alıcı + satıcı rolleri). */
export const getCompanyLinks = async () => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/company-links`);
  return response.data as { success: boolean; data: CompanyLinkRow[] };
};

/**
 * Bu firmanın ALICI rolünde aktif B2B bağlantısı olan şube id'leri.
 * Bir şube bir B2B satıcıya giriş yaptığında (veya şube adına kod alındığında)
 * CompanyTradeLink.BuyerBranchId dolar; bu liste o şubelerin Siparişler menüsünü
 * otomatik açmak için kullanılır. Hafif uç (sidebar her açılışta çağırır).
 */
export const getBuyerBranchIds = async () => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/company-links/buyer-branch-ids`);
  return response.data as { success: boolean; branchIds: string[] };
};

const sameBranchId = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Bu şubenin B2B alıcı bağlantısı / gelen siparişi var mı?
 * Yeni uç yoksa (eski ecommerce API) mevcut company-links + incoming-orders ile yedekler.
 */
export const hasBuyerB2BForBranch = async (branchId: string): Promise<boolean> => {
  if (!branchId?.trim()) return false;

  try {
    const res = await getBuyerBranchIds();
    if (res?.success && Array.isArray(res.branchIds) && res.branchIds.some((id) => sameBranchId(id, branchId))) {
      return true;
    }
  } catch {
    // Eski API'de uç yok (404) — aşağıdaki yedekler devreye girer.
  }

  try {
    const links = await getCompanyLinks();
    if (links?.data?.some(
      (l) => l.role === 'buyer' && l.status === 'Active' && sameBranchId(l.buyerBranchId, branchId)
    )) {
      return true;
    }
  } catch {
    // bağlantı listesi okunamadı
  }

  try {
    const orders = await getIncomingPurchaseOrders({ branchId, page: 1, pageSize: 1 });
    if ((orders?.pagination?.total ?? 0) > 0) return true;
  } catch {
    // sipariş listesi okunamadı
  }

  return false;
};

/** Bağlantıyı keser (yeni siparişler aynalanmaz; mevcut kayıtlar durur). */
export const revokeCompanyLink = async (id: number) => {
  const response = await ecommerceClient.post(`${API_BASE_URL}/company-links/delete`, { Id: id });
  return response.data as { success: boolean; message?: string };
};

/**
 * Kesilmiş bir bağlantıyı aynı kayıt üzerinden yeniden aktif eder. Otomatik yollar
 * (tarama / Aquasoft girişi) kesilen bağlantıyı geri getirmez; tek geri dönüş yolu budur.
 */
export const reconnectCompanyLink = async (id: number) => {
  const response = await ecommerceClient.post(`${API_BASE_URL}/company-links/reconnect`, { Id: id });
  return response.data as { success: boolean; message?: string };
};

export interface AquasoftCompanySearchRow {
  companyId: string;
  name: string;
  taxNumberMasked?: string | null;
}

/** ELLE eşleştirme için Aquasoft firma araması (min 3 karakter; kendi firma hariç, en fazla 20 sonuç). */
export const searchAquasoftCompanies = async (q: string) => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/company-links/companies/search`, { params: { q } });
  return response.data as { success: boolean; data: AquasoftCompanySearchRow[] };
};

export interface UnlinkedAccountRow {
  id: number;
  name: string;
  accountCode?: string | null;
  taxNumber?: string | null;
  email?: string | null;
}

/** ELLE eşleştirme için BAĞLANTISIZ cari araması (aktif bağlantısı olanlar listelenmez). */
export const searchUnlinkedAccounts = async (q?: string) => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/company-links/accounts/search`, {
    params: q ? { q } : undefined,
  });
  return response.data as { success: boolean; data: UnlinkedAccountRow[] };
};

/** ELLE eşleştirme: seçilen cariyi seçilen Aquasoft firmasıyla bağlar (güvenlik ağı). */
export const createManualCompanyLink = async (accountId: number, buyerCompanyId: string) => {
  const response = await ecommerceClient.post(`${API_BASE_URL}/company-links/manual-link`, {
    AccountId: accountId,
    BuyerCompanyId: buyerCompanyId,
  });
  return response.data as { success: boolean; message?: string; linkId?: number };
};

/**
 * Gelen alış siparişleri listesi (alıcı rolündeki şirket için).
 * branchId: şube id → o şubenin siparişleri; 'hq' → yalnız merkez (şubesiz) siparişler.
 */
export const getIncomingPurchaseOrders = async (params?: {
  status?: string;
  invoiced?: boolean;
  branchId?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/incoming-purchase-orders`, { params });
  return response.data as {
    success: boolean;
    data: IncomingPurchaseOrderRow[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  };
};

export const getIncomingPurchaseOrderDetail = async (id: string) => {
  const response = await ecommerceClient.get(`${API_BASE_URL}/incoming-purchase-orders/${id}`);
  return response.data as { success: boolean; data: IncomingPurchaseOrderDetail };
};

/** Kalemi kendi katalogdaki ürünle eşle (inventoryItemId=null → eşlemeyi kaldır). */
export const matchIncomingOrderItem = async (orderId: string, itemId: string, inventoryItemId: string | null) => {
  const response = await ecommerceClient.post(
    `${API_BASE_URL}/incoming-purchase-orders/${orderId}/items/${itemId}/match`,
    { InventoryItemId: inventoryItemId }
  );
  return response.data as { success: boolean; message?: string };
};

/** Kalem eşleme için katalog araması (e-ticaret filtresi YOK — tüm aktif ürünler). */
export const searchCatalogItemsForMatch = async (search: string, pageSize = 20) => {
  const response = await accountingApi.post(`${INVENTORY_API_BASE_URL}/items/paged`, {
    pageNumber: 1,
    pageSize,
    searchTerm: search?.trim() || undefined,
    isActive: true,
    excludeVariantChildren: true,
    sortColumn: 'name',
    sortDirection: 'asc',
  });
  const payload = response?.data || {};
  const items = payload.items || payload.Items || [];
  return (Array.isArray(items) ? items : []).map((it: any) => ({
    id: String(it.id ?? it.Id ?? ''),
    name: String(it.name ?? it.Name ?? ''),
    stockCode: (it.stockCode ?? it.StockCode ?? null) as string | null,
    barcode: (it.barcode ?? it.Barcode ?? null) as string | null,
    categoryName: (it.categoryName ?? it.CategoryName ?? null) as string | null,
  })).filter((it: { id: string }) => it.id);
};

/** Faturalaşmış eşleşmeyen kaleme sonradan ürün eşler ve stok girişi yapar. */
export const lateStockEntryForIncomingOrderItem = async (
  orderId: string,
  itemId: string,
  payload: { inventoryItemId?: string; autoCreate?: boolean; warehouseId?: string }
) => {
  const response = await ecommerceClient.post(
    `${API_BASE_URL}/incoming-purchase-orders/${orderId}/items/${itemId}/late-stock-entry`,
    {
      InventoryItemId: payload.inventoryItemId || null,
      AutoCreate: payload.autoCreate ?? false,
      WarehouseId: payload.warehouseId || null,
    }
  );
  return response.data as { success: boolean; message?: string };
};

/** Alıcı panelinde teslim onayı — kalem bazlı teslim/hasar + ayna + satıcı siparişi delivered olur. */
export const confirmIncomingOrderDelivery = async (
  orderId: string,
  payload?: {
    note?: string | null;
    lines?: Array<{
      orderItemId: string;
      receivedQuantity: number;
      damagedQuantity: number;
      reason?: string | null;
    }>;
  }
) => {
  const response = await ecommerceClient.post(
    `${API_BASE_URL}/incoming-purchase-orders/${orderId}/confirm-delivery`,
    {
      note: payload?.note ?? null,
      lines: payload?.lines ?? [],
    }
  );
  return response.data as {
    success: boolean;
    message?: string;
    idempotent?: boolean;
    data?: { receiptStatus?: string; hasDiscrepancy?: boolean; invoiced?: boolean };
  };
};

/** İçe aktarılan alış faturasını gelen B2B siparişe bağlar (fatura kesmez). */
export const linkImportedIncomingOrderInvoice = async (orderId: string, invoiceId: string) => {
  const response = await ecommerceClient.post(
    `${API_BASE_URL}/incoming-purchase-orders/${orderId}/link-imported-invoice`,
    { InvoiceId: invoiceId }
  );
  return response.data as {
    success: boolean;
    message?: string;
    idempotent?: boolean;
    data?: { invoiceId: string; invoiceNumber?: string; supplierAccountId?: number | null };
  };
};

/** Satıcı e-fatura gönderiminden sonra B2B aynasını tazeler. */
export const refreshB2BMirrorByInvoice = async (invoiceId: string) => {
  try {
    await ecommerceClient.post(`${API_BASE_URL}/orders/refresh-b2b-mirror-by-invoice/${invoiceId}`, {});
  } catch {
    // best-effort — gönderim başarısını bozmaz
  }
};

/** Gelen siparişi alış faturasına çevirir (stok girişi + tedarikçi carisi). */
export const convertIncomingOrderToInvoice = async (
  orderId: string,
  options?: { warehouseId?: string; dueDate?: string; autoCreateProducts?: boolean; skipLotStock?: boolean }
) => {
  const response = await ecommerceClient.post(
    `${API_BASE_URL}/incoming-purchase-orders/${orderId}/convert`,
    {
      WarehouseId: options?.warehouseId,
      DueDate: options?.dueDate,
      AutoCreateProducts: options?.autoCreateProducts ?? false,
      SkipLotStock: options?.skipLotStock ?? false,
    }
  );
  return response.data as {
    success: boolean;
    message?: string;
    idempotent?: boolean;
    data?: {
      invoiceId: string;
      invoiceNumber?: string;
      supplierAccountId?: number;
      supplierAccountName?: string;
      unmatchedItemCount?: number;
    };
  };
};

export default {
  getHeaderConfig,
  saveHeaderConfig,
  getMenuConfig,
  saveMenuConfig,
  getMenuCategories,
  saveMenuCategory,
  reorderMenuCategories,
  getGeneralConfig,
  saveGeneralConfig,
  getLanguageSettings,
  saveLanguageSettings,
  getGoogleSearchSettings,
  saveGoogleSearchSettings,
  getSiteBackgrounds,
  getActiveSiteBackground,
  saveSiteBackground,
  getProductModules,
  saveProductModule,
  deleteProductModule,
  getEcommerceProducts, // ✅ YENİ
  getProductsByIds, // ✅ YENİ
  getCategoriesByIds, // ✅ YENİ
  getSliderSettings, // ✅ SLIDER
  saveSliderSettings, // ✅ SLIDER
  addSlide, // ✅ SLIDER
  updateSlide, // ✅ SLIDER
  toggleSlide, // ✅ SLIDER
  deleteSlide, // ✅ SLIDER
  getCategorySettings, // ✅ CATEGORY SETTINGS
  saveCategorySettings, // ✅ CATEGORY SETTINGS
  getProductSettings, // ✅ PRODUCT SETTINGS
  saveProductSettings, // ✅ PRODUCT SETTINGS
  getProductDetailSettings, // ✅ PRODUCT DETAIL SETTINGS
  saveProductDetailSettings, // ✅ PRODUCT DETAIL SETTINGS
  getFooterSettings, // ✅ FOOTER SETTINGS
  getContactSettings, // ✅ CONTACT SETTINGS
  saveContactSettings, // ✅ CONTACT SETTINGS
  getRestaurantSettings, // ✅ RESTAURANT SETTINGS
  saveRestaurantSettings, // ✅ RESTAURANT SETTINGS
  updateRestaurantBranch, // ✅ RESTAURANT BRANCHES (Branches tablosundan)
  saveFooterSettings, // ✅ FOOTER SETTINGS
  getCartSettings, // ✅ CART SETTINGS
  saveCartSettings, // ✅ CART SETTINGS
  getLoginPageSettings, // ✅ LOGIN PAGE SETTINGS
  saveLoginPageSettings, // ✅ LOGIN PAGE SETTINGS
  getAboutSettings, // ✅ ABOUT SETTINGS
  saveAboutSettings, // ✅ ABOUT SETTINGS
  getAboutTabs, // ✅ ABOUT TABS (Dynamic Pages)
  saveAboutTab, // ✅ ABOUT TABS (Dynamic Pages)
  deleteAboutTab, // ✅ ABOUT TABS (Dynamic Pages)
  getCustomPages, // ✅ CUSTOM PAGES (Kullanıcı tanımlı sayfalar)
  saveCustomPage, // ✅ CUSTOM PAGES
  deleteCustomPage, // ✅ CUSTOM PAGES
  getLegalDocuments, // ✅ LEGAL DOCS (Yasal Sözleşmeler)
  saveLegalDocument, // ✅ LEGAL DOCS
  deleteLegalDocument, // ✅ LEGAL DOCS
  seedLegalDefaults, // ✅ LEGAL DOCS
  getCustomForms, // ✅ CUSTOM FORMS (Kullanıcı tanımlı formlar)
  saveCustomForm, // ✅ CUSTOM FORMS
  deleteCustomForm, // ✅ CUSTOM FORMS
  getCustomFormSubmissions, // ✅ CUSTOM FORMS - Submissions
  markCustomFormSubmissionRead, // ✅ CUSTOM FORMS - Submissions
  deleteCustomFormSubmission, // ✅ CUSTOM FORMS - Submissions
  getShowcaseAreas,
  getShowcaseArea,
  createShowcaseArea,
  updateShowcaseArea,
  toggleShowcaseArea,
  deleteShowcaseArea,
  getCampaigns, // ✅ KAMPANYA
  getCampaign, // ✅ KAMPANYA
  createCampaign, // ✅ KAMPANYA
  updateCampaign, // ✅ KAMPANYA
  toggleCampaign, // ✅ KAMPANYA
  deleteCampaign, // ✅ KAMPANYA
  getCampaignStats, // ✅ KAMPANYA
  getCampaignUsages,
  simulateCampaignCart,
};

